import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Menu,
} from "@mui/material";
import { Close as CloseIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import { BarChart, Info, MoreVertical, Trash2 } from 'lucide-react'; // Using BarChart as a placeholder icon, added MoreVertical, Trash2
import axios from 'axios';
import { API_URL } from '../../globals';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import PosthogTestModal from './PosthogTestModal';

const PosthogModal = ({ open, onClose }) => {
  const { session } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchMappingsLoading, setFetchMappingsLoading] = useState(false);
  const [studioOptions, setStudioOptions] = useState([]);
  const [formData, setFormData] = useState({
    host: '',
    api_key: '',
    project_id: '',
    studio_id: '',
  });
  const [fetchStudiosLoading, setFetchStudiosLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedStudioId, setSelectedStudioId] = useState(null);

  useEffect(() => {
    if (open && session?.access_token) {
      fetchMappings();
      fetchStudios();
    } else {
      // Reset state when modal closes or session is unavailable
      setMappings([]);
      setStudioOptions([]);
      setFormData({ host: '', api_key: '', project_id: '', studio_id: '' });
    }
  }, [open, session?.access_token]);

  const fetchStudios = async () => {
    setFetchStudiosLoading(true);
    try {
      const authToken = session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await axios.get(`${API_URL}/studio/seeds`, { headers });

      if (response.data) {
        const studios = response.data.filter(studio => studio.status === "READY" || studio.status === "BUSY");
        const options = studios.map((studio) => ({
          label: studio.display_name,
          value: studio.id,
          type: "studio",
          data: studio,
        }));
        setStudioOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch studios:', error);
      // Optionally show an error message to the user
    } finally {
      setFetchStudiosLoading(false);
    }
  };

  const fetchMappings = async () => {
    setFetchMappingsLoading(true);
    try {
      const authToken = session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await axios.get(`${API_URL}/studio/posthog-mappings`, { headers });

      if (response.data?.mappings) {
        setMappings(response.data.mappings);
      } else {
        setMappings([]); // Ensure mappings is an array even if the key is missing
      }
    } catch (error) {
        if (error.response && error.response.status === 404) {
            // If 404, it means no mappings exist, which is not an error state
            setMappings([]);
        } else if (error.response && error.response.status === 500 && error.response.data.error === 'Failed to retrieve PostHog mappings') {
             // Handle specific case where backend confirms no mappings exist or retrieval failed expectedly
             setMappings([]);
             console.log("No PostHog mappings found for user or failed to retrieve.");
        }
         else {
            console.error('Failed to fetch PostHog mappings:', error);
            // Optionally show an error message to the user
        }
    } finally {
      setFetchMappingsLoading(false);
    }
  };

 const handleAddMapping = async () => {
    if (!formData.studio_id || !formData.host || !formData.api_key || !formData.project_id) {
      return;
    }

    setLoading(true);
    try {
      const authToken = session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };

      const response = await axios.post(
        `${API_URL}/studio/posthog-mapping`,
        {
          host: formData.host,
          api_key: formData.api_key,
          project_id: formData.project_id,
          studio_id: formData.studio_id,
        },
        { headers }
      );

      if (response.status === 201) {
        await fetchMappings();
        setSelectedStudioId(formData.studio_id);
        setShowTestModal(true);
        setFormData(prev => ({
          ...prev,
          host: '',
          api_key: '',
          project_id: '',
        }));
      }
    } catch (error) {
        if (error.response && error.response.status === 409) {
             alert('A PostHog mapping already exists for this studio.');
        } else {
            console.error('Failed to add PostHog mapping:', error);
            alert('Failed to add PostHog mapping. Please check the details and try again.');
        }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async () => {
    if (!selectedMapping) return;

    setDeleteLoading(true);
    try {
      const authToken = session.access_token;
      const headers = { Authorization: `Bearer ${authToken}` };

      const response = await axios.delete(`${API_URL}/studio/posthog-mapping/${selectedMapping.id}`, { headers });

      if (response.status === 200) {
        await fetchMappings(); // Refresh list after delete
      }
    } catch (error) {
      console.error('Failed to delete PostHog mapping:', error);
      alert('Failed to delete mapping. Please try again.');
    } finally {
      setDeleteLoading(false);
      handleMenuClose(); // Close menu after action
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStudioChange = (event) => {
    setFormData(prev => ({
      ...prev,
      studio_id: event.target.value
    }));
  };

  const getStudioDisplayName = (studioId) => {
    const studio = studioOptions.find(option => option.value === studioId);
    return studio?.label || `Studio ID: ${studioId}`; // Fallback if name not found
  };

  // Helper to mask API key
  const maskApiKey = (apiKey) => {
    if (!apiKey || apiKey.length < 8) return '********';
    return `${apiKey.substring(0, 4)}...${apiKey.substring(apiKey.length - 4)}`;
  };

  const handleMenuOpen = (event, mapping) => {
    setAnchorEl(event.currentTarget);
    setSelectedMapping(mapping);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMapping(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#121212',
            backgroundImage: 'none',
            borderRadius: 1,
            border: '1px solid #2a2a2a',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          borderBottom: '1px solid #2a2a2a'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BarChart size={24} style={{ color: '#fff' }} /> {/* Placeholder Icon */}
            <Box>
              <Typography sx={{ color: '#fff', fontSize: '1.25rem', fontWeight: 600, mb: 0.5 }}>
                PostHog Integration
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
                Connect your PostHog project
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#666', '&:hover': { color: '#fff', backgroundColor: '#252525' }}}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{
          p: 0,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#333', borderRadius: '4px' }
        }}>
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Informational Box */}
            <Box sx={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 1, p: 3 }}>
              <Typography sx={{ color: '#888', fontSize: '0.875rem', lineHeight: 1.5 }}>
                Connect Fume to your PostHog instance to allow Studios to access product session recording data. You will need your PostHog Host URL, Project ID, and an API Key.
                <Box component="ul" sx={{ mt: 2, pl: 2 }}>
                  <li>Select the Studio you want to link.</li>
                  <li>Enter your PostHog Host Host Domain (e.g., `us.posthog.com` or your self-hosted Domain).</li>
                  <li>Enter your PostHog Project ID.</li>
                  <li>Enter a PostHog Personal API Key.</li>
                  <li>Click "Add Mapping".</li>
                </Box>
                 <Typography sx={{ color: '#666', fontSize: '0.75rem', mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                   <Info size={12}/> API keys are stored securely and encrypted.
                 </Typography>
              </Typography>
            </Box>

            {/* Existing Mappings */}
             <Box sx={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 1, p: 3 }}>
               <Typography sx={{ color: '#888', fontSize: '0.875rem', fontWeight: 600, mb: 2 }}>
                 Existing Mappings
               </Typography>
               {fetchMappingsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} sx={{ color: '#666' }} />
                  </Box>
               ) : mappings.length > 0 ? (
                 mappings.map((mapping, index) => (
                   <Box
                     key={mapping.id || index} // Use mapping.id if available
                     sx={{
                       p: 2,
                       border: '1px solid #2a2a2a',
                       borderRadius: 1,
                       mb: 1,
                       color: '#fff',
                       display: 'flex',
                       alignItems: 'center',
                       gap: 1,
                       '&:last-child': { mb: 0 }
                     }}
                   >
                     <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                       <Typography sx={{ color: '#888', fontSize: '0.875rem', fontWeight: 500 }}>
                         {getStudioDisplayName(mapping.studio_id)}
                       </Typography>
                       <Typography sx={{ color: '#666' }}>→</Typography>
                       <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                         {mapping.host} (Project: {mapping.project_id})
                       </Typography>
                        <Typography sx={{ color: '#666', fontSize: '0.875rem', ml: 1 }}>
                           API Key: {maskApiKey(mapping.api_key)} {/* Display masked key */}
                        </Typography>
                     </Box>
                     {/* Delete Menu Button */}
                     <IconButton
                       onClick={(e) => handleMenuOpen(e, mapping)}
                       disabled={deleteLoading && selectedMapping?.id === mapping.id} // Disable button while deleting this specific item
                       sx={{ 
                         color: '#666',
                         p: 1,
                         '&:hover': {
                           color: '#fff',
                           backgroundColor: '#252525'
                         }
                       }}
                     >
                        {deleteLoading && selectedMapping?.id === mapping.id ? (
                          <CircularProgress size={16} sx={{ color: '#666' }}/>
                        ) : (
                          <MoreVertical size={16} />
                        )}
                     </IconButton>
                   </Box>
                 ))
               ) : (
                 <Typography sx={{ color: '#666', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>
                   No PostHog mappings found.
                 </Typography>
               )}
             </Box>


            {/* Add New Mapping Form */}
            <Box sx={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 1, p: 3 }}>
              <Typography sx={{ color: '#888', fontSize: '0.875rem', fontWeight: 600, mb: 3 }}>
                Add New PostHog Mapping
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Studio Selector */}
                <FormControl fullWidth>
                  <InputLabel id="studio-select-label" sx={{ color: '#888', '&.Mui-focused': { color: '#888' } }}>
                    Select Studio
                  </InputLabel>
                  <Select
                    labelId="studio-select-label"
                    value={formData.studio_id}
                    onChange={handleStudioChange}
                    label="Select Studio"
                    disabled={fetchStudiosLoading}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2a2a' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#888' },
                      color: '#fff',
                      '& .MuiSelect-icon': { color: '#666' },
                    }}
                    MenuProps={{ // Style the dropdown menu
                       PaperProps: {
                          sx: {
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #2a2a2a',
                            color: '#fff',
                             '& .MuiMenuItem-root': {
                               '&:hover': {
                                 backgroundColor: '#252525',
                               },
                               '&.Mui-selected': {
                                 backgroundColor: '#252525 !important', // Ensure selection style override
                               },
                             },
                          },
                       },
                    }}
                  >
                    {fetchStudiosLoading ? (
                       <MenuItem disabled value="">
                          <CircularProgress size={20} sx={{ mr: 1, color: '#666' }}/> Loading Studios...
                     </MenuItem>
                    ) : studioOptions.length === 0 ? (
                     <MenuItem disabled value="">No available studios found</MenuItem>
                    ) : (
                      studioOptions.map((option) => (
                         <MenuItem key={option.value} value={option.value}>
                          {option.label}
                         </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* PostHog Details Inputs */}
                 <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      backgroundColor: '#121212', // Slightly darker inner background
                      borderRadius: 1,
                      p: 2,
                      border: '1px solid #2a2a2a'
                   }}>
                      <Typography sx={{ color: '#888', fontSize: '0.875rem', fontWeight: 500, mb: 1 }}>
                          PostHog Details
                      </Typography>
                      <TextField
                          fullWidth
                          label="Host Domain"
                          name="host"
                          value={formData.host}
                          onChange={handleInputChange}
                          placeholder="us.posthog.com"
                          sx={textFieldStyles}
                          InputLabelProps={{ sx: inputLabelStyles }}
                          InputProps={{ sx: inputBaseStyles }}
                      />
                      <TextField
                          fullWidth
                          label="API Key"
                          name="api_key"
                          type="password" // Use password type to obscure
                          value={formData.api_key}
                          onChange={handleInputChange}
                          placeholder="Enter your PostHog Personal API Key"
                          sx={textFieldStyles}
                          InputLabelProps={{ sx: inputLabelStyles }}
                          InputProps={{ sx: inputBaseStyles }}
                      />
                      <TextField
                          fullWidth
                          label="Project ID"
                          name="project_id"
                          value={formData.project_id}
                          onChange={handleInputChange}
                          placeholder="Enter your PostHog Project ID"
                          sx={textFieldStyles}
                          InputLabelProps={{ sx: inputLabelStyles }}
                          InputProps={{ sx: inputBaseStyles }}
                      />
                  </Box>

                {/* Add Mapping Button */}
                <Button
                  onClick={handleAddMapping}
                  disabled={loading || !formData.studio_id || !formData.host || !formData.api_key || !formData.project_id || fetchStudiosLoading}
                  sx={{
                    width: '100%',
                    height: '48px',
                    border: '1px solid #333',
                    backgroundColor: 'transparent',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#252525' },
                    '&.Mui-disabled': { backgroundColor: 'transparent', color: '#666', border: '1px solid #2a2a2a' }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#fff'}} /> : 'Add Mapping'}
                </Button>
              </Box>
            </Box>

          </Box>
        </DialogContent>

        {/* Footer/Cancel Button */}
        <Box sx={{
          p: 3,
          borderTop: '1px solid #2a2a2a',
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor: '#121212', // Match Dialog background
          position: 'sticky', // Keep cancel button visible
          bottom: 0,
          zIndex: 1 // Ensure it's above scrolled content
        }}>
          <Button onClick={onClose} sx={{ color: '#888', '&:hover': { backgroundColor: '#252525', color: '#fff' } }}>
            Cancel
          </Button>
        </Box>

        {/* Delete Confirmation Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: 1,
              minWidth: 120,
            }
          }}
        >
          <MenuItem 
            onClick={handleDeleteMapping}
            disabled={deleteLoading}
            sx={{
              color: '#ff4d4f', // Red color for delete
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#252525'
              },
              gap: 1,
              display: 'flex',
              alignItems: 'center',
              '&.Mui-disabled': {
                color: '#ff4d4f',
                opacity: 0.5
              }
            }}
          >
            {deleteLoading ? (
              <CircularProgress size={16} sx={{ color: '#ff4d4f' }} />
            ) : (
              <Trash2 size={16} />
            )}
            Delete
          </MenuItem>
        </Menu>
      </Dialog>

      <PosthogTestModal
        open={showTestModal}
        onClose={(newGroupId) => {
          setShowTestModal(false);
          if (newGroupId) {
            onClose(newGroupId);
          }
        }}
        studioId={selectedStudioId}
      />
    </>
  );
};

// Common styles for TextFields
const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: '#2a2a2a' },
    '&:hover fieldset': { borderColor: '#666' },
    '&.Mui-focused fieldset': { borderColor: '#888' },
  },
};
const inputLabelStyles = {
    color: '#888',
    '&.Mui-focused': { color: '#888' },
};
const inputBaseStyles = { // For styling the input element itself if needed
    backgroundColor: '#1a1a1a', // Match inner box background slightly
    borderRadius: 1,
};


export default PosthogModal;
